import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  PurchaseOrder,
  Supplier,
} from "@/redux/services/inventory/inventory.type";
import {
  useGetSupplierQuery,
  useGetSuppliersQuery,
} from "@/redux/services/inventory/supplier.api";
import { useUpdatePurchaseOrderMutation } from "@/redux/services/inventory/purchaseOrder.api";
import { useDebounce } from "@/hooks/useDebounce";
import AsyncCombobox from "@/components/inventory/AsyncCombobox";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/apiErrors";

interface EditPurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  restaurantId: string;
  /**
   * Limited edit for received POs: only the purchase date and notes are
   * editable; supplier and expected-delivery are hidden and left untouched.
   */
  limited?: boolean;
}

const EditPurchaseOrderModal = ({
  isOpen,
  onClose,
  purchaseOrder,
  restaurantId,
  limited = false,
}: EditPurchaseOrderModalProps) => {
  const [supplierId, setSupplierId] = useState(
    purchaseOrder.supplier?.id ?? ""
  );
  const [orderDate, setOrderDate] = useState(
    purchaseOrder.orderDate.slice(0, 10)
  );
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(
    purchaseOrder.expectedDeliveryDate?.slice(0, 10) ?? ""
  );
  const [notes, setNotes] = useState(purchaseOrder.notes ?? "");

  useEffect(() => {
    if (!isOpen) return;
    setSupplierId(purchaseOrder.supplier?.id ?? "");
    setOrderDate(purchaseOrder.orderDate.slice(0, 10));
    setExpectedDeliveryDate(
      purchaseOrder.expectedDeliveryDate?.slice(0, 10) ?? ""
    );
    setNotes(purchaseOrder.notes ?? "");
  }, [isOpen, purchaseOrder]);

  // Supplier search
  const [supplierSearch, setSupplierSearch] = useState("");
  const debouncedSupplierSearch = useDebounce(supplierSearch, 300);
  const { data: suppliersData, isFetching: isFetchingSuppliers } =
    useGetSuppliersQuery(
      {
        restaurantId,
        query: debouncedSupplierSearch.trim()
          ? { search: debouncedSupplierSearch.trim() }
          : {},
      },
      { skip: limited }
    );
  const suppliers = suppliersData?.results ?? [];

  // Always fetch the currently-selected supplier so the label sticks
  const { data: selectedSupplier } = useGetSupplierQuery(
    { supplierUuid: supplierId },
    { skip: limited || !supplierId }
  );

  const supplierCache = useRef<Map<string, Supplier>>(new Map());
  suppliers.forEach((s) => supplierCache.current.set(s.id, s));
  if (selectedSupplier) supplierCache.current.set(selectedSupplier.id, selectedSupplier);

  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  );

  const [updatePO, { isLoading }] = useUpdatePurchaseOrderMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!limited && !supplierId) {
      toast.error("Sélectionnez un fournisseur");
      return;
    }
    if (!orderDate) {
      toast.error("La date d'achat est requise");
      return;
    }

    const res = await updatePO({
      uuid: purchaseOrder.id,
      data: limited
        ? { orderDate, notes: notes.trim() || null }
        : {
            supplier: `/api/suppliers/${supplierId}`,
            orderDate,
            expectedDeliveryDate: expectedDeliveryDate || null,
            notes: notes.trim() || null,
          },
    });

    if ("data" in res) {
      toast.success("Bon de commande modifié");
      onClose();
    } else if ("error" in res) {
      toastApiError(res.error, "Erreur lors de la modification");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Modifier le bon de commande"
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-muted-foreground">
          {limited
            ? "Vous pouvez modifier la date d'achat et les notes."
            : "Seuls le fournisseur, les dates et les notes sont modifiables. Pour changer les lignes, supprimez et recréez le bon."}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {!limited && (
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Fournisseur <span className="text-danger">*</span>
              </label>
              <AsyncCombobox
                value={supplierId}
                onChange={setSupplierId}
                options={supplierOptions}
                selectedLabel={supplierCache.current.get(supplierId)?.name}
                placeholder="Sélectionner un fournisseur"
                searchPlaceholder="Rechercher par nom, contact, téléphone..."
                emptyMessage="Aucun fournisseur"
                isLoading={isFetchingSuppliers}
                onSearchChange={setSupplierSearch}
              />
            </div>
          )}
          <div>
            <label
              htmlFor="edit-po-order-date"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              {limited ? "Date d'achat" : "Date de commande"}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              id="edit-po-order-date"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          {!limited && (
            <div>
              <label
                htmlFor="edit-po-expected-date"
                className="block text-sm font-medium text-card-foreground mb-1"
              >
                Livraison prévue
              </label>
              <input
                id="edit-po-expected-date"
                type="date"
                value={expectedDeliveryDate}
                onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}
          <div className="sm:col-span-2 lg:col-span-3">
            <label
              htmlFor="edit-po-notes"
              className="block text-sm font-medium text-card-foreground mb-1"
            >
              Notes
            </label>
            <textarea
              id="edit-po-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Instructions particulières, contact, etc."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditPurchaseOrderModal;
